import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  loginRequest,
  logoutRequest,
  sendLoginOtpRequest,
  verifyLoginOtpRequest,
  type LoginOtpPayload,
  type LoginPayload,
  type VerifyLoginOtpPayload,
} from '@/features/auth/api/authApi';
import { applySession, getApiMessage } from '@/features/auth/api/session';
import { getErrorMessage } from '@/shared/api/errors';
import { clearClientSession } from '@/shared/lib/auth/clearClientSession';
import { useAppDispatch } from '@/shared/store/hooks';
import { setCredentials } from '@/shared/store/slices/authSlice';
import { CLIENT_ROLE, isTeamMemberRole } from '@/shared/config/roles';

function finishSignIn(
  data: unknown,
  dispatch: ReturnType<typeof useAppDispatch>,
  navigate: ReturnType<typeof useNavigate>,
) {
  const user = applySession(data);
  if (user) dispatch(setCredentials({ user }));
  const home =
    user?.role === CLIENT_ROLE || isTeamMemberRole(user?.role)
      ? '/campaigns'
      : '/dashboard';
  navigate(home, { replace: true });
  window.setTimeout(() => {
    toast.success('Signed in successfully');
  }, 100);
}

export function useLoginMutation() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: LoginPayload) => loginRequest(payload),
    onSuccess: (data) => finishSignIn(data, dispatch, navigate),
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Login failed'));
    },
  });
}

export function useSendLoginOtpMutation() {
  return useMutation({
    mutationFn: (payload: LoginOtpPayload) => sendLoginOtpRequest(payload),
    onSuccess: (data) => {
      toast.success(getApiMessage(data, 'OTP sent successfully'));
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to send OTP'));
    },
  });
}

export function useVerifyLoginOtpMutation() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (payload: VerifyLoginOtpPayload) =>
      verifyLoginOtpRequest(payload),
    onSuccess: (data) => finishSignIn(data, dispatch, navigate),
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Invalid OTP'));
    },
  });
}

export function useLogoutMutation() {
  return useMutation({
    mutationFn: async () => {
      const { getCachedFcmToken, clearCachedFcmToken } = await import(
        '@/shared/lib/firebase/messaging'
      );
      const { removeFcmToken } = await import(
        '@/features/notification/api/fcmApi'
      );
      const token = getCachedFcmToken();
      if (token) {
        try {
          await removeFcmToken({ token });
        } catch {
          // Best-effort cleanup before logout
        }
        clearCachedFcmToken();
      }
      return logoutRequest();
    },
    onSettled: () => {
      clearClientSession();
      window.setTimeout(() => {
        toast.success('Signed out successfully');
      }, 100);
    },
  });
}
